import { Controller } from '@hotwired/stimulus'
import { renderPdf, watchContainerWidth } from '../lib/pdf_renderer'
import {
  projectPlacement as computeProjection,
  applyProjection,
} from '../lib/placement_projection'

export default class extends Controller {
  static targets = ['container', 'loading']
  static values = {
    documentId: Number,
    url: String,
    maxWidth: { type: Number, default: 480 },
    placements: { type: Array, default: [] },
  }

  async connect() {
    if (!this.hasContainerTarget || !this.urlValue) return

    this.rendering = false
    await this.renderAndPlace()
    this.stopWatching = watchContainerWidth(
      this.containerTarget,
      () => this.rerender()
    )
  }

  disconnect() {
    if (this.stopWatching) this.stopWatching()
  }

  async renderAndPlace() {
    try {
      const renderedPages = await renderPdf(
        this.urlValue,
        this.containerTarget
      )
      this.pagesMap = new Map(
        renderedPages.map((page) => {
          const wrapper = this.wrapCanvas(page.canvas)
          return [page.page, { ...page, wrapper }]
        })
      )
      this.placeAll()
    } catch (error) {
      console.error('PDF preview failed', error)
    } finally {
      if (this.hasLoadingTarget) {
        this.loadingTarget.classList.add('hidden')
      }
    }
  }

  async rerender() {
    if (this.rendering) return
    this.rendering = true
    try {
      this.containerTarget.innerHTML = ''
      await this.renderAndPlace()
    } finally {
      this.rendering = false
    }
  }

  wrapCanvas(canvas) {
    const wrapper = document.createElement('div')
    wrapper.style.position = 'relative'
    wrapper.style.display = 'block'
    canvas.parentNode.insertBefore(wrapper, canvas)
    wrapper.appendChild(canvas)
    return wrapper
  }

  placeAll() {
    this.placementElements = []
    this.placementsValue.forEach((placement) => {
      const pageInfo = this.pagesMap.get(Number(placement.page_number))
      if (!pageInfo) return

      const overlay = document.createElement('div')
      overlay.className = [
        'absolute rounded-md border-2 border-dashed overflow-hidden',
        'flex items-center justify-center pointer-events-none',
      ].join(' ')
      overlay.style.cssText = [
        `background-color: ${placement.color || '#028090'}1A`,
        `border-color: ${placement.color || '#028090'}`,
        `color: ${placement.color || '#028090'}`,
      ].join(';')

      const label = document.createElement('span')
      label.className = 'block w-full truncate px-1 text-center font-medium'
      label.textContent =
        placement.signer_name || placement.signer_ref || ''
      overlay.appendChild(label)

      pageInfo.wrapper.appendChild(overlay)
      const projected = {
        x: Number(placement.x),
        y: Number(placement.y),
        width: Number(placement.width),
        height: Number(placement.height),
      }
      this.projectOverlay(overlay, projected, pageInfo)
      this.placementElements.push({ overlay, placement: projected, pageInfo })
    })
  }

  projectOverlay(overlay, placement, pageInfo) {
    const projection = computeProjection(placement, pageInfo)
    applyProjection(overlay, projection)
    const fontPx = Math.max(9, Math.min(16, projection.cssHeight * 0.22))
    overlay.style.fontSize = `${fontPx}px`
  }
}
